import type { DesignItem } from './types';
import { resolveItemBounds } from './extensionRegistry';
import { measureTextLayerBounds } from './textMeasurement';
import { normalizeSingleLineText } from '../designer-app/utils/textContent';

/**
 * Size of what a layer actually paints, which can be larger than its stored box: a text box
 * saved for another font size or area, a long name typed into a narrow box, addon layouts.
 * Addon resolvers (curved text, typography) win; plain text is measured the way the design
 * store refits a text box. Null for layers whose box is their content (images, shapes), and
 * for text whose web font has not loaded yet.
 */
export function measureItemContent(item: DesignItem): { width: number; height: number } | null {
  const fromAddon = resolveItemBounds(item);
  if (fromAddon && fromAddon.width > 0 && fromAddon.height > 0) return fromAddon;
  if (item.type !== 'text' || typeof document === 'undefined') return null;
  const ex = item as Record<string, unknown>;
  const family = String(ex.fontFamily ?? 'Arial');
  const size = Number(ex.fontSize ?? 24);
  const weight = String(ex.fontWeight ?? 'normal');
  const style = String(ex.fontStyle ?? 'normal');
  // A web font still loading would be measured with its fallback: report nothing until it lands
  // (Preflight re-runs on `document.fonts` loadingdone).
  if (document.fonts && !document.fonts.check(`${style} ${weight} ${size}px "${family.replace(/"/g, '')}"`)) return null;
  return measureTextLayerBounds(normalizeSingleLineText(item.text) || ' ', family, size, {
    fontWeight: weight,
    fontStyle: style,
    lineHeight: Number(ex.lineHeight ?? 1.2),
    letterSpacing: Number(ex.letterSpacing ?? 0),
    textDecoration: String(ex.textDecoration ?? 'none'),
    textAlign: String(ex.textAlign ?? 'center'),
  });
}
